import { TypographyH1 } from "@/components/ui/typography"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import {
  Grid3X3,
  List,
  Play,
  FileText,
  Globe,
  Type,
  Camera,
} from "lucide-react"
import {
  ArchivedList,
  CreateContentWithTextModal,
  CreateContentWithWebUrlModal,
  Library,
  TempContentGrid,
} from "@/widgets"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { type ButtonHTMLAttributes, type ReactNode } from "react"
import { cn } from "@/lib/utils"
import { useCreateNoteImages, useCreateNoteUploadFile } from "@/features"
import {
  useContentCacheStore,
  useGetBanner,
  useAutoTranslation,
  useModalStore,
  useUserConfigurationsStore,
  type LibraryTab,
  type UserLibraryViewMode,
} from "@/entities"
import { Modal } from "@/shared/types"
import styles from "./styles.module.scss"
import {
  DISCOVERED_TRANSLATION_KEYS,
  LANG_KEYS,
  MOBILE_TRANSLATION_KEYS,
} from "@/shared/constants"

const CreateContentButton = ({
  className,
  title,
  subtitle,
  icon: Icon,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  title: string
  subtitle: string
  icon?: React.ComponentType<{ className?: string }>
}) => {
  return (
    <button
      type="button"
      className={cn(
        // Base styles
        "group relative flex cursor-pointer flex-col gap-3 rounded-lg border p-4 text-left",
        "bg-card border-border",
        // Hover effects (Stripe-style lift + glow)
        "transition-all duration-300",
        "hover:border-primary/30 hover:-translate-y-1 hover:shadow-lg",
        // Active state
        "active:translate-y-0 active:shadow-md",
        className
      )}
      {...props}
    >
      {/* Gradient overlay on hover */}
      <div className="from-primary/5 absolute inset-0 rounded-lg bg-gradient-to-br to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

      {/* Icon container */}
      {Icon && (
        <div className="bg-primary/10 text-primary group-hover:bg-primary/20 relative flex h-10 w-10 items-center justify-center rounded-lg transition-colors">
          <Icon className="h-5 w-5" />
        </div>
      )}

      <div className="relative">
        <span className="text-sm font-semibold">{title}</span>
        <span className="text-muted-foreground mt-1 block text-xs">
          {subtitle}
        </span>
      </div>
    </button>
  )
}

export const LibraryPage = () => {
  const { t } = useAutoTranslation()
  const { handleFileUpload } = useCreateNoteUploadFile()
  const { handleImagesUpload } = useCreateNoteImages()
  const openModal = useModalStore(s => s.openModal)
  const viewMode = useUserConfigurationsStore(s => s.viewMode)
  const setViewMode = useUserConfigurationsStore(s => s.setViewMode)
  const libraryTab = useContentCacheStore(s => s.libraryTab)
  const setLibraryTab = useContentCacheStore(s => s.setLibraryTab)
  const { data: bannerData } = useGetBanner()
  const banner = bannerData?.banner

  const handleWebUrlCreation = () => {
    openModal(
      Modal.CREATE_CONTENT_WITH_WEB_URL,
      <CreateContentWithWebUrlModal />
    )
  }

  const handleTextCreation = () => {
    openModal(Modal.CREATE_CONTENT_WITH_TEXT, <CreateContentWithTextModal />)
  }

  const CreationElements = [
    {
      title: t(LANG_KEYS.DOCUMENTS),
      subtitle: t(DISCOVERED_TRANSLATION_KEYS.LISTEN_TO_DOCUMENTS_SUBTITLE),
      onClick: handleFileUpload,
      icon: FileText,
    },
    {
      title: t(LANG_KEYS.WEB_ARTICLES_SITES),
      subtitle: t(DISCOVERED_TRANSLATION_KEYS.WEB_ARTICLES_SUBTITLE),
      onClick: handleWebUrlCreation,
      icon: Globe,
    },
    {
      title: t(LANG_KEYS.PASTED_TEXT),
      subtitle: t(DISCOVERED_TRANSLATION_KEYS.PASTED_TEXT_SUBTITLE),
      onClick: handleTextCreation,
      icon: Type,
    },
    {
      title: t(DISCOVERED_TRANSLATION_KEYS.IMAGES),
      subtitle: t(DISCOVERED_TRANSLATION_KEYS.IMAGES_SUBTITLE),
      onClick: handleImagesUpload,
      icon: Camera,
    },
  ]

  const LibraryTabs: { value: LibraryTab; label: string }[] = [
    { value: "all", label: t(MOBILE_TRANSLATION_KEYS.ALL_CONTENT_TAB) },
    { value: "extension", label: t(MOBILE_TRANSLATION_KEYS.FROM_EXTENSION) },
    { value: "archived", label: t(LANG_KEYS.ARCHIVED) },
  ]

  const ChangeViewModeElements: {
    mode: UserLibraryViewMode
    icon: ReactNode
  }[] = [
    {
      mode: "grid",
      icon: <Grid3X3 size={18} />,
    },
    {
      mode: "list",
      icon: <List size={18} />,
    },
  ]

  return (
    <div className={styles.libraryPage}>
      <TypographyH1 className={styles.libraryPage__title}>
        {t(LANG_KEYS.WHAT_DO_YOU_WANT_TO_LISTEN_TO)}
      </TypographyH1>
      {banner && (
        <Alert variant="destructive" className={styles.banner}>
          <AlertTitle>{banner.title}</AlertTitle>
          <AlertDescription>{banner.text}</AlertDescription>
        </Alert>
      )}
      <div className={styles.creationBtnsWrapper}>
        {CreationElements.map(({ title, subtitle, onClick, icon }) => (
          <CreateContentButton
            key={`CREATE_CONTENT_BUTTON_${title}`}
            onClick={onClick}
            title={title}
            subtitle={subtitle}
            icon={icon}
          />
        ))}
      </div>

      <TempContentGrid />

      <div className={styles.libraryHeader}>
        <div className={styles.libraryHeader__title}>
          <Play />
          <h3>{t(DISCOVERED_TRANSLATION_KEYS.RECENT_FILES)}</h3>
        </div>

        <Tabs
          value={libraryTab}
          onValueChange={value => setLibraryTab(value as LibraryTab)}
          className={styles.libraryTabs}
        >
          <TabsList>
            {LibraryTabs.map(({ value, label }) => (
              <TabsTrigger key={`LIBRARY_TAB_${value}`} value={value}>
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div className={styles.viewMode}>
          {ChangeViewModeElements.map(({ mode, icon }) => (
            <button
              type="button"
              className={cn(styles.viewMode__item, {
                [styles.viewMode__item_active]: viewMode === mode,
              })}
              key={`VIEW_MODE_BUTTON_${mode}`}
              onClick={() => setViewMode(mode)}
            >
              {icon}
            </button>
          ))}
        </div>
      </div>
      {libraryTab === "all" && <Library filter="all" />}
      {libraryTab === "extension" && <Library filter="extension" />}
      {libraryTab === "archived" && <ArchivedList />}
    </div>
  )
}
